'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useDateFormatter } from '@terra/ui-formatters';
import { formatCloudProviderLabel } from '@terra/shared-contracts-integrations';
import type {
  DnsZoneSurfaceAttachBodyDto,
  DnsZoneSurfaceAttachmentDto,
  DnsZoneSurfaceDetachBodyDto,
  DnsZoneSurfaceProviderBodyDto,
  UpdateDnsZonePolicyBodyDto,
} from '@terra/shared-contracts-external-network';
import { MetaDefault } from '../../../components/typography';
import { ConfirmationDialog } from '../../confirmation-dialog/ConfirmationDialog';
import { SettingsCard } from '../../settings/SettingsCard';
import { toasterApi } from '../../toast/ToastProvider';
import { DnsZoneCatalogRow } from './DnsZoneCatalogRow';
import { InventoryIntegrationsLink } from './InventoryIntegrationsLink';
import { InventoryLoadFailedAlert } from './InventoryLoadFailedAlert';
import { InventoryNotConnectedCard } from './InventoryNotConnectedCard';
import {
  attachmentKey,
  createDnsZoneAutoScopeCollection,
  toAttachBody,
  toDetachBody,
  toSyncBody,
} from './dns-zone-attach.utils';
import { notifyInventoryFailure } from './notify-inventory-failure';

export type DnsZonePendingState = {
  isAttaching: boolean;
  isDetaching: boolean;
  isSyncing: boolean;
  isUpdatingPolicy: boolean;
  isVerifying: boolean;
};

export type DnsZoneAttachSectionProps = {
  attachments: DnsZoneSurfaceAttachmentDto[];
  isLoading: boolean;
  isError: boolean;
  isReadOnly: boolean;
  pending: DnsZonePendingState;
  onAttach: (body: DnsZoneSurfaceAttachBodyDto) => Promise<unknown>;
  onDetach: (body: DnsZoneSurfaceDetachBodyDto) => Promise<unknown>;
  onSync: (body: DnsZoneSurfaceProviderBodyDto) => Promise<unknown>;
  onVerify: (input: { zoneId: string }) => Promise<unknown>;
  onUpdatePolicy: (input: {
    zoneId: string;
    body: UpdateDnsZonePolicyBodyDto;
  }) => Promise<unknown>;
  onOpenIntegrations: () => void;
};

export function DnsZoneAttachSection({
  attachments,
  isLoading,
  isError,
  isReadOnly,
  pending,
  onAttach,
  onDetach,
  onSync,
  onVerify,
  onUpdatePolicy,
  onOpenIntegrations,
}: DnsZoneAttachSectionProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalInventory.dnsZones',
  );
  const tShared = useTranslations(
    'designSystem.networkSettings.externalInventory.shared',
  );
  const dateFormatter = useDateFormatter();
  const [pendingAttachZoneId, setPendingAttachZoneId] = useState<string | null>(
    null,
  );
  const isBusy =
    pending.isAttaching ||
    pending.isDetaching ||
    pending.isSyncing ||
    pending.isUpdatingPolicy ||
    pending.isVerifying;
  const scopeCollection = useMemo(
    () =>
      createDnsZoneAutoScopeCollection({
        labels: {
          IN_SCOPE: t('scope.IN_SCOPE'),
          OUT_OF_SCOPE: t('scope.OUT_OF_SCOPE'),
        },
      }),
    [t],
  );
  const pendingAttachment = attachments.find(
    (attachment) => attachment.zoneId === pendingAttachZoneId,
  );

  const handleAttach = async (attachment: DnsZoneSurfaceAttachmentDto) => {
    const body = toAttachBody(attachment, 'IN_SCOPE');

    if (!body) {
      return;
    }

    try {
      await onAttach(body);
      toasterApi.success({ title: t('attachSuccess') });
    } catch (error) {
      notifyInventoryFailure({ title: t('attachFailed'), error });
    }
  };

  const handleDetach = async (attachment: DnsZoneSurfaceAttachmentDto) => {
    const body = toDetachBody(attachment);

    if (!body) {
      return;
    }

    try {
      await onDetach(body);
      toasterApi.success({ title: t('detachSuccess') });
    } catch (error) {
      notifyInventoryFailure({ title: t('detachFailed'), error });
    }
  };

  const handleSync = async (attachment: DnsZoneSurfaceAttachmentDto) => {
    try {
      await onSync(toSyncBody(attachment));
      toasterApi.success({ title: tShared('runSyncSuccess') });
    } catch (error) {
      notifyInventoryFailure({
        title: tShared('runSyncFailed'),
        error,
        fallbackDescription: tShared('runSyncFailedDescription'),
      });
    }
  };

  const handleVerify = async (attachment: DnsZoneSurfaceAttachmentDto) => {
    if (!attachment.zoneId) {
      return;
    }

    try {
      await onVerify({ zoneId: attachment.zoneId });
      toasterApi.success({ title: t('recheckSuccess') });
    } catch (error) {
      notifyInventoryFailure({ title: t('recheckFailed'), error });
    }
  };

  const handlePolicyChange = async ({
    zoneId,
    body,
  }: {
    zoneId: string;
    body: UpdateDnsZonePolicyBodyDto;
  }) => {
    try {
      await onUpdatePolicy({ zoneId, body });
      toasterApi.success({ title: t('updateSuccess') });
    } catch (error) {
      notifyInventoryFailure({ title: t('updateFailed'), error });
    }
  };

  if (isError) {
    return <InventoryLoadFailedAlert message={t('loadFailed')} />;
  }

  if (!isLoading && attachments.length === 0) {
    return (
      <InventoryNotConnectedCard
        title={t('notConnectedTitle')}
        description={t('notConnectedDescription')}
        onOpenIntegrations={onOpenIntegrations}
      />
    );
  }

  return (
    <>
      <SettingsCard
        isLoading={isLoading}
        footer={
          <MetaDefault color="fg.neutral.subtle">
            {t.rich('iamHelp', {
              link: (chunks) => (
                <InventoryIntegrationsLink onClick={onOpenIntegrations}>
                  {chunks}
                </InventoryIntegrationsLink>
              ),
            })}
          </MetaDefault>
        }
      >
        {attachments.map((attachment) => (
          <DnsZoneCatalogRow
            key={attachmentKey(attachment)}
            attachment={attachment}
            isReadOnly={isReadOnly}
            isBusy={isBusy}
            isSyncing={pending.isSyncing}
            isAttaching={pending.isAttaching}
            isDetaching={pending.isDetaching}
            isVerifying={pending.isVerifying}
            lastSyncedText={
              attachment.lastSyncedAt
                ? t('lastSynced', {
                    date: dateFormatter.format(
                      new Date(attachment.lastSyncedAt),
                    ),
                  })
                : t('neverSynced')
            }
            neverSyncedLabel={t('neverSynced')}
            scopeCollection={scopeCollection}
            onAttach={(next) => setPendingAttachZoneId(next.zoneId)}
            onDetach={(next) => void handleDetach(next)}
            onSync={(next) => void handleSync(next)}
            onVerify={(next) => void handleVerify(next)}
            onEnabledChange={({ zoneId, autoScope, enabled }) =>
              void handlePolicyChange({
                zoneId,
                body: { autoScope, enabled },
              })
            }
            onAutoScopeChange={({ zoneId, autoScope }) =>
              void handlePolicyChange({ zoneId, body: { autoScope } })
            }
            labels={{
              enabled: t('enabledLabel'),
              scopePlaceholder: t('scopePlaceholder'),
              attach: t('attach', {
                provider: formatCloudProviderLabel({
                  provider: attachment.provider,
                }),
              }),
              detach: t('detach'),
              detachAria: tShared('detach'),
              runSync: tShared('runSync'),
              recheck: t('recheck'),
              privateBadge: t('privateBadge'),
              status: (status) => t(`status.${status}`),
              delegation: (status) => t(`delegation.${status}`),
              recordCount: (count) => t('recordCount', { count }),
            }}
          />
        ))}
      </SettingsCard>
      <ConfirmationDialog
        isOpen={pendingAttachZoneId !== null}
        onOpenChange={({ open }) => {
          if (!open) {
            setPendingAttachZoneId(null);
          }
        }}
        variant="warning"
        title={t('attachConfirmTitle')}
        description={
          pendingAttachment
            ? t('attachConfirmDescription', {
                zone: pendingAttachment.zoneName ?? '',
                count: pendingAttachment.recordCount ?? 0,
              })
            : t('attachConfirmTitle')
        }
        confirmText={t('attachConfirm')}
        onConfirm={() => {
          if (!pendingAttachment) {
            return;
          }

          setPendingAttachZoneId(null);
          void handleAttach(pendingAttachment);
        }}
      />
    </>
  );
}
